import type { ThemeMode } from "../theme/theme";
import type { ThemeDefinition } from "../../../../../../shared/frontend/theme/themeCatalog";

const PLATFORM_DARWIN = "darwin";
const PLATFORM_WIN32 = "win32";

export type DesktopAppMenuPosition = {
  x: number;
  y: number;
};

export type M1DesktopBridge = {
  platform?: string;
  windowId?: string | null;
  hasWindowControlsOverlay?: boolean;
  onSetThemeMode: (callback: (mode: ThemeMode) => void) => () => void;
  notifyThemeMode: (mode: ThemeMode) => void;
  notifyThemeChrome?: (theme: { mode: ThemeMode; background: string; overlay: string; symbol: string }) => void;
  notifyInteractionBlocked?: (blocked: boolean) => void;
  retry?: () => void;
  flushStorage?: () => void;
  titlebarIconSrc?: string;
  openAppMenu?: (position?: DesktopAppMenuPosition) => void;
  onAppMenuClosed?: (callback: () => void) => () => void;
  toggleTheme?: () => void;
  minimizeWindow?: () => void;
  toggleMaximizeWindow?: () => void;
  closeWindow?: () => void;
  isWindowMaximized?: () => boolean;
  onWindowMaximizeChange?: (callback: (maximized: boolean) => void) => () => void;
};

declare global {
  interface Window {
    m1desktop?: M1DesktopBridge;
  }
}

export function isM1DesktopShell(): boolean {
  return typeof window !== "undefined" && window.m1desktop != null;
}

export function desktopWindowId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return window.m1desktop?.windowId ?? undefined;
}

export function desktopPlatform(): string {
  if (typeof window === "undefined") return "";
  return window.m1desktop?.platform ?? "";
}

export function desktopUsesHtmlCaptionButtons(): boolean {
  if (!isM1DesktopShell()) return false;
  const platform = desktopPlatform();
  if (platform === PLATFORM_DARWIN || platform === PLATFORM_WIN32) return false;
  if (typeof window !== "undefined" && window.m1desktop?.hasWindowControlsOverlay === true) return false;
  return true;
}

export function desktopTitlebarIconSrc(): string {
  if (typeof window === "undefined") return "";
  return window.m1desktop?.titlebarIconSrc ?? "";
}

export function openDesktopAppMenu(position?: DesktopAppMenuPosition): void {
  if (typeof window === "undefined") return;
  window.m1desktop?.openAppMenu?.(position);
}

export function notifyDesktopInteractionBlocked(blocked: boolean): void {
  if (typeof window !== "undefined") window.m1desktop?.notifyInteractionBlocked?.(blocked);
}

export function subscribeDesktopAppMenuClosed(callback: () => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  return window.m1desktop?.onAppMenuClosed?.(callback) ?? (() => undefined);
}

export function subscribeDesktopThemeMode(callback: (mode: ThemeMode) => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  const unsubscribe = window.m1desktop?.onSetThemeMode?.(callback) ?? (() => undefined);
  const onEvent = (event: Event) => {
    const mode = (event as CustomEvent<ThemeMode>).detail;
    if (mode === "light" || mode === "dark") callback(mode);
  };
  window.addEventListener("m1desktop:theme-mode", onEvent);
  return () => {
    unsubscribe();
    window.removeEventListener("m1desktop:theme-mode", onEvent);
  };
}

export function notifyDesktopTheme(theme: ThemeDefinition): void {
  if (typeof window === "undefined") return;
  const bridge = window.m1desktop;
  if (bridge?.notifyThemeChrome) {
    bridge.notifyThemeChrome({ mode: theme.brightness, background: theme.vars.bg, overlay: theme.vars.rail, symbol: theme.vars["rail-strong"] });
  } else {
    bridge?.notifyThemeMode?.(theme.brightness);
  }
}

export function desktopMinimizeWindow(): void {
  if (typeof window === "undefined") return;
  window.m1desktop?.minimizeWindow?.();
}

export function desktopToggleMaximizeWindow(): void {
  if (typeof window === "undefined") return;
  window.m1desktop?.toggleMaximizeWindow?.();
}

export function desktopCloseWindow(): void {
  if (typeof window === "undefined") return;
  window.m1desktop?.closeWindow?.();
}

export function desktopIsWindowMaximized(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.m1desktop?.isWindowMaximized?.() === true;
  } catch {
    return false;
  }
}

export function subscribeDesktopWindowMaximize(callback: (maximized: boolean) => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  return window.m1desktop?.onWindowMaximizeChange?.(callback) ?? (() => undefined);
}
